// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CheckSquare, Square, Layers } from 'lucide-react';
import './MergerPeriodSelector.css';

const MergerPeriodSelector = ({ 
  mergerData, // { discoveryResult, files, sheetSelections, statementType }
  onProcess,
  onError, 
  onBack, 
  loading,
  hasExistingResults = false,
  onViewResults
}) => {
  const [selectedPeriods, setSelectedPeriods] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);

  const { discoveryResult, files, sheetSelections, statementType, selectedConfig } = mergerData;
  const { merged_periods, sheet_sources, total_periods } = discoveryResult;

  // Auto-select all periods when component mounts
  useEffect(() => {
    if (merged_periods) {
      const allPeriodIndices = new Set(merged_periods.map(p => p.period_index));
      setSelectedPeriods(allPeriodIndices);
      setSelectAll(true);
    }
  }, [merged_periods]);

  const handlePeriodToggle = (periodIndex) => {
    const newSelected = new Set(selectedPeriods);
    
    if (newSelected.has(periodIndex)) {
      newSelected.delete(periodIndex);
    } else {
      newSelected.add(periodIndex);
    }
    
    setSelectedPeriods(newSelected);
    setSelectAll(newSelected.size === merged_periods.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPeriods(new Set());
      setSelectAll(false);
    } else {
      const allPeriodIndices = new Set(merged_periods.map(p => p.period_index));
      setSelectedPeriods(allPeriodIndices);
      setSelectAll(true);
    }
  };

  const handleProcess = async () => {
    if (selectedPeriods.size === 0) {
      onError('Please select at least one period to process');
      return;
    }
    
    const selectedPeriodIndices = Array.from(selectedPeriods);
    onProcess(selectedPeriodIndices, files, sheetSelections, statementType, selectedConfig);
  };

  // Get source keys that have data for a period
  const getSourceKeysForPeriod = (period) => {
    const keys = new Set(period.sources.map(s => s.source_key));
    return Array.from(keys).sort();
  };

  // Get color for source key
  const getSourceKeyColor = (key) => {
    const colors = {
      'A': 'var(--accent)',
      'B': '#22c55e',
      'C': '#3b82f6',
      'D': '#a855f7',
      'E': '#f59e0b',
      'F': '#06b6d4',
    };
    return colors[key] || 'var(--muted)';
  };

  return (
    <div className="merger-period-selector-container">
      <div className="merger-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={16} />
          Back
        </button>
        <div className="header-content">
          <h2>Select Merged Periods</h2>
          <p className="step-description">
            Periods have been aligned across {sheet_sources.length} sheets. Select which periods to include in the merged P&L.
          </p>
        </div>
      </div>

      {/* Source Legend */}
      <div className="source-legend">
        <h4><Layers size={16} /> Source Sheets</h4>
        <div className="source-list">
          {sheet_sources.map(source => (
            <div key={source.source_key} className="source-item">
              <span 
                className="source-badge"
                style={{ background: getSourceKeyColor(source.source_key) }}
              >
                {source.source_key}
              </span>
              <span className="source-name">{source.file_name}</span>
              <span className="source-sheet">({source.sheet_name})</span>
            </div>
          ))}
        </div>
      </div>

      <div className="file-info-summary">
        <h3>Merge Summary</h3>
        <div className="info-grid">
          <div className="info-item"><strong>Files:</strong> {files.length}</div>
          <div className="info-item"><strong>Sheets:</strong> {sheet_sources.length}</div>
          <div className="info-item"><strong>Total Periods:</strong> {total_periods}</div>
          <div className="info-item"><strong>Selected:</strong> {selectedPeriods.size}</div>
          <div className="info-item">
            <strong>Configuration:</strong> {selectedConfig?.config_name || selectedConfig?.tenant_name || 'Default'}
          </div>
        </div>
      </div>

      {/* Select All Controls */}
      <div className="select-all-section">
        <button 
          onClick={handleSelectAll}
          className={`select-all-button ${selectAll ? 'selected' : ''}`}
          disabled={loading}
        >
          {selectAll ? <CheckSquare size={16} /> : <Square size={16} />}
          {selectAll ? 'Deselect All' : 'Select All'} ({merged_periods.length} periods)
        </button>
        <p className="select-help">
          All periods are selected by default. Periods with multiple source keys contain data from multiple sheets.
        </p>
      </div>

      <div className="period-selection">
        <h3>Aligned Periods</h3>
        <div className="stacked-panel">
          <table className="period-table">
            <thead>
              <tr>
                <th></th>
                <th>Period</th>
                <th>Sources</th>
                <th>Original Names</th>
              </tr>
            </thead>
            <tbody>
              {merged_periods.map((period) => {
                const isSelected = selectedPeriods.has(period.period_index);
                const sourceKeys = getSourceKeysForPeriod(period);
                const originalNames = period.sources.map(s => `[${s.source_key}] ${s.original_name}`).join(', ');

                return (
                  <tr
                    key={period.period_index}
                    className={isSelected ? 'selected' : ''}
                    onClick={() => handlePeriodToggle(period.period_index)}
                  >
                    <td className="check-cell">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); handlePeriodToggle(period.period_index); }}
                      />
                    </td>
                    <td className="date-cell">{period.display_name}</td>
                    <td className="sources-cell">
                      <div className="source-keys">
                        {sourceKeys.map(key => (
                          <span 
                            key={key}
                            className="source-key-badge"
                            style={{ background: getSourceKeyColor(key) }}
                            title={sheet_sources.find(s => s.source_key === key)?.file_name}
                          >
                            {key}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="name-cell">{originalNames}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="action-buttons">
        {hasExistingResults && onViewResults && (
          <button
            onClick={onViewResults}
            className="view-results-button"
            disabled={loading}
          >
            View Results
            <ArrowRight size={16} />
          </button>
        )}
        <button
          onClick={handleProcess}
          className="process-button multi-period"
          disabled={selectedPeriods.size === 0 || loading}
        >
          {loading ? (
            <>
              <div className="button-spinner"></div>
              Merging & Mapping...
            </>
          ) : (
            <>
              Merge & Map {selectedPeriods.size} Period{selectedPeriods.size !== 1 ? 's' : ''}
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MergerPeriodSelector;
